// Setup › Price book's pictures in Flowology's house style (BL-109), in Blueprint only:
//   PricebookArtPanel: an item's picture in its sheet (ui-web's PricebookArt, from pricebook.art.get);
//   PricebookArtLine: the whole book's at a glance, "Images: 1,212 ready · 48 making · 3 need a look", and
//     for those who edit, "Make the missing ones", which says how many and what they'd cost first
//     (pricebook.art.queueMissing without, then with, confirm). It sits in Setup › Price book's Pictures
//     section, under the switches (BL-141; that section is its own zone, so its buttons aren't counted
//     with the book's), and asks again each time `enabled` changes.
// The panel is mounted in Setup.tsx (the item sheet), the line in Settings.tsx; neither draws in Riverstone.
import { useEffect, useMemo, useState } from 'react';
import { money, PricebookArt, PricebookArtProps } from '@flowology/ui-web';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { useDesign } from '../blueprint/design.ts';
import { useShownTheme } from '../blueprint/theme.ts';
import { refusal } from '../history/customer-edit-model.ts';

export function PricebookArtPanel({ itemId, name }: { itemId: string; name: string }) {
  const bp = useDesign() === 'blueprint';
  // in the light or dark the page draws, and again when that changes while the sheet is open
  const theme = useShownTheme();
  const host = useMemo(() => ({ ...officeHost(() => undefined), theme }), [theme]);
  const [data, setData] = useState<PricebookArtProps | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!bp) return;
    let live = true;
    client
      .call('pricebook.art.get', { itemId })
      .then((r) => {
        const p = PricebookArtProps.safeParse(r);
        if (!live) return;
        if (p.success) setData(p.data);
        else setError('Its picture can’t be shown here.');
      })
      .catch((e: unknown) => {
        if (live) setError(refusal(e));
      });
    return () => {
      live = false;
    };
  }, [bp, itemId]);
  if (!bp) return null;
  return (
    <div className="flw flw-office pb-art">
      {error ? (
        <p className="flw-sub">{error}</p>
      ) : (
        data && <PricebookArt data={data} host={host} name={name} />
      )}
    </div>
  );
}

interface Status {
  enabled: boolean;
  ready: number;
  making: number;
  needsALook: number;
  missing: number;
  /** distinct live pictures: items that look alike share one */
  pictures: number;
  /** close matches from Flowology's picture library waiting for a person (BL-109 v3) */
  offered?: number;
  /** new pictures waiting for the owner or a manager to approve them (BL-122) */
  toApprove?: number;
  paused: boolean;
}

/** queueMissing's preview: pictures to make (`count`) for `items` items, `unnamed` still worked out. */
interface Missing {
  count: number;
  items: number;
  restyle: number;
  unnamed: number;
  estimateCents?: number;
}

const n = (x: number) => x.toLocaleString('en-CA');

/** What "Make the missing ones" would do, in words: items, the pictures they share, what it costs. */
function missingWords(m: Missing): string {
  const said = [`${n(m.items)} items have no picture in the current style yet.`];
  if (m.count)
    said.push(
      `Items that look alike share one, so that’s ${n(m.count)} pictures${
        m.restyle ? ` (${n(m.restyle)} have one in an older style, which stays until its new one passes)` : ''
      }.`,
    );
  if (m.unnamed)
    said.push(
      `What ${n(m.unnamed)} of them show is still being worked out; their pictures are counted then.`,
    );
  if (m.count)
    said.push(
      `Making a light and a dark one for each${
        m.estimateCents != null ? ` costs about ${money(Math.round(m.estimateCents))}` : ''
      }.`,
    );
  said.push('Past this month’s budget, making waits for next month.');
  return said.join(' ');
}

export function PricebookArtLine({
  canEdit,
  enabled,
}: {
  canEdit: boolean;
  /** pictures' switch as the page has it: the line is asked for again when it changes */
  enabled?: boolean;
}) {
  const bp = useDesign() === 'blueprint';
  const [s, setS] = useState<Status | null>(null);
  const [asking, setAsking] = useState<Missing | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!bp) return;
    let live = true;
    client
      .call<Status>('pricebook.art.status', {})
      .then((r) => live && setS(r))
      .catch(() => live && setS(null));
    return () => {
      live = false;
    };
  }, [bp, version, enabled]);
  if (!bp || !s) return null;
  const ask = async () => {
    setBusy(true);
    setSaid(null);
    try {
      setAsking(await client.call<Missing>('pricebook.art.queueMissing', {}));
    } catch (e) {
      setSaid(refusal(e));
    }
    setBusy(false);
  };
  const make = async () => {
    setBusy(true);
    try {
      const r = await client.call<{ queued: number }>('pricebook.art.queueMissing', { confirm: true });
      setSaid(
        r.queued ? `Making ${n(r.queued)} pictures.` : 'The rest are made once what they show is worked out.',
      );
      setAsking(null);
      setVersion((v) => v + 1);
    } catch (e) {
      setSaid(refusal(e));
    }
    setBusy(false);
  };
  return (
    <div className="flw flw-office pb-art-line" role="status">
      <p className="flw-sub">
        {s.enabled
          ? `Images: ${n(s.ready)} items ready (${n(s.pictures)} pictures) · ${n(s.making)} making · ${n(s.needsALook)} need a look${s.offered ? ` · ${n(s.offered)} from the library to pick` : ''}${s.toApprove ? ` · ${n(s.toApprove)} to approve` : ''}`
          : 'Images are off: no pictures are made until they’re turned on.'}
        {s.enabled && s.paused && ' · paused: this month’s image budget is used'}
      </p>
      {canEdit && s.enabled && s.missing > 0 && !asking && (
        <div className="flw-acts">
          <button type="button" className="flw-btn" disabled={busy} onClick={() => void ask()}>
            Make the missing ones
          </button>
        </div>
      )}
      {asking && (
        <div className="flw-waits">
          <p>{asking.items ? missingWords(asking) : 'Every item has its picture, or one on its way.'}</p>
          <div className="flw-acts">
            {asking.items > 0 && (
              <button
                type="button"
                className="flw-btn flw-btn-pri"
                disabled={busy}
                onClick={() => void make()}
              >
                {busy ? 'Queuing…' : asking.count ? `Make ${n(asking.count)}` : 'Make them'}
              </button>
            )}
            <button type="button" className="flw-btn" onClick={() => setAsking(null)}>
              Not now
            </button>
          </div>
        </div>
      )}
      {said && <p className="flw-sub">{said}</p>}
    </div>
  );
}
